import * as reducerFuncs from 'redux/modules/superreports.reducers';
import { EntityDataKey } from 'types/reports';
import { reducerWithInitialState } from 'typescript-fsa-reducers';
import { getSignatureFromDraftAndQuery } from 'utils/superreports';
import { initialState } from './superreports.initialState';
import type { State } from './superreports.types';
import * as actionTypes from './superreports.types';

export { initialState } from './superreports.initialState';

const reducer = reducerWithInitialState(initialState)
  .caseWithAction(actionTypes.updateReportDraft, reducerFuncs.updateReportDraft)
  .caseWithAction(
    actionTypes.clearReportDraft,
    (state): State => ({
      ...state,
      draft: {
        widgets: [],
      },
      draftSignature: null,
    })
  )
  .caseWithAction(
    actionTypes.emailedReport,
    (state, action): State => ({
      ...state,
      emailedReport: action.payload,
    })
  )
  .caseWithAction(
    actionTypes.dismissEmailedReport,
    (state): State => ({ ...state, emailedReport: null })
  )
  .caseWithAction(actionTypes.setQuery, reducerFuncs.setQuery)
  .caseWithAction(actionTypes.fetchedWidgetHours, reducerFuncs.fetchedWidgetHours)
  .caseWithAction(actionTypes.fetchWidgetHoursError, reducerFuncs.fetchWidgetHoursError)
  .caseWithAction(actionTypes.addWidgetToDraft, reducerFuncs.addWidgetToDraft)
  .caseWithAction(actionTypes.editWidgetOnDraft, reducerFuncs.editWidgetOnDraft)
  .caseWithAction(actionTypes.removeWidgetOnDraft, reducerFuncs.removeWidgetOnDraft)
  .caseWithAction(actionTypes.updateWidgetColumns, reducerFuncs.setWidgetColumns)
  .caseWithAction(actionTypes.createReportDraft, reducerFuncs.createReportDraft)

  // To track modified state of a draft, we'll generate a signature here
  // that we can check against in selectors. I _think_ this is an OK solution
  // (One) alternative is setting a modified flag on every draft update, query.filters update,
  // widget column/ordering update --- feels very messy, so trying this approach:
  .caseWithAction(
    actionTypes.setDraftSignature,
    (state): State => ({
      ...state,
      draftSignature: getSignatureFromDraftAndQuery(state.draft, state.query),
    })
  )

  .caseWithAction(actionTypes.changeWidgetType, reducerFuncs.changeWidgetType)
  .caseWithAction(
    actionTypes.increaseSequenceStartingWith,
    reducerFuncs.increaseSequenceStartingWith
  )
  .caseWithAction(
    actionTypes.decreaseSequenceStartingWith,
    reducerFuncs.decreaseSequenceStartingWith
  )
  .caseWithAction(actionTypes.expandAndLoadRowOnWidget, reducerFuncs.expandRowOnWidget)
  .caseWithAction(actionTypes.collapseRowOnWidget, reducerFuncs.collapseRowOnWidget)
  .caseWithAction(actionTypes.resetWidgetStatusOnDraft, reducerFuncs.resetWidgetStatusOnDraft)
  .caseWithAction(actionTypes.fetchedReports, reducerFuncs.fetchedReports)
  .caseWithAction(actionTypes.fetchedReport, reducerFuncs.fetchedReport)
  .caseWithAction(actionTypes.createdNewReport, reducerFuncs.createdNewReport)
  .caseWithAction(actionTypes.updateWidgetOptions, reducerFuncs.updateWidgetOptions)
  .caseWithAction(actionTypes.fetchedReportData, reducerFuncs.fetchedReportData)
  .caseWithAction(actionTypes.deleteReport, reducerFuncs.deleteReport)
  .caseWithAction(
    actionTypes.setReportFilter,
    (state, action): State => ({
      ...state,
      reportFilter: { ...state.reportFilter, ...action.payload },
    })
  )
  .caseWithAction(actionTypes.updatedExistingReport, reducerFuncs.updatedExistingReport)
  .caseWithAction(actionTypes.fetchedPublicReports, reducerFuncs.fetchedPublicReports)
  .caseWithAction(actionTypes.createdNewPublicReport, reducerFuncs.createdNewPublicReport)
  .caseWithAction(actionTypes.setPublicReportFilter, (state, action): State => {
    return {
      ...state,
      publicReportFilter: { ...state.publicReportFilter, ...action.payload },
    };
  })
  .caseWithAction(actionTypes.deletePublicReport, reducerFuncs.deletePublicReport)
  .caseWithAction(
    actionTypes.undeletePublicReport,
    (state, action): State => ({
      ...state,
      publicReportResult: [...state.publicReportResult, action.payload],
    })
  )
  .caseWithAction(
    actionTypes.togglePublicReportView,
    (state, action): State => ({
      ...state,
      reportView: action.payload ?? state.reportView,
    })
  )
  .caseWithAction(
    actionTypes.publicReportListFilter,
    (state, action): State => ({
      ...state,
      reportListFilter: action.payload ?? state.reportListFilter,
    })
  )

  .case(actionTypes.moveWidget, reducerFuncs.moveWidget)
  .case(
    actionTypes.saveTimesheetQuery,
    (state): State => ({
      ...state,
      timesheetQuery: { ...state.query },
    })
  )
  .case(
    actionTypes.restoreTimesheetQuery,
    (state): State => ({
      ...state,
      query: { ...state.timesheetQuery, page: 1 },
    })
  )
  .case(
    actionTypes.saveClientReportQuery,
    (state): State => ({
      ...state,
      clientReportQuery: { ...state.query },
    })
  )
  .case(
    actionTypes.restoreClientReportQuery,
    (state): State => ({
      ...state,
      query: { ...state.clientReportQuery },
    })
  )
  .case(actionTypes.setClientReportQuery, (state, payload): State => {
    // Always ensure resource is set to 'client'
    const updatedClientReportQuery = {
      ...state.clientReportQuery,
      ...payload,
      client_ids: Array.isArray(payload.client_ids) ? payload.client_ids : [],
      // Ensure filters get properly merged and resource is always 'client'
      filters: {
        ...state.clientReportQuery.filters,
        ...(payload.filters || {}),
        resource: 'client', // Force resource to always be 'client'
      },
    };

    return {
      ...state,
      // Update both query states to keep them in sync
      query: updatedClientReportQuery,
      clientReportQuery: updatedClientReportQuery,
    };
  })
  .case(
    actionTypes.fetchedTimesheetData,
    (state, maybeHasMore): State => ({
      ...state,
      timesheetMaybeHasMore: maybeHasMore,
    })
  )
  .case(
    actionTypes.fetchedHourSummaryData,
    (state, maybeHasMore): State => ({
      ...state,
      hourSummaryMaybeHasMore: maybeHasMore,
    })
  )
  .case(
    actionTypes.retrieveTotalHoursCount,
    (state, timesheetTotalCount): State => ({
      ...state,
      timesheetTotalCount,
    })
  )
  .case(
    actionTypes.setTimesheetColumns,
    (state, timesheetColumns): State => ({
      ...state,
      timesheetColumns: Array.from(new Set(timesheetColumns)),
    })
  )
  .case(
    actionTypes.setTimesheetTaskColumns,
    (state, timesheetTaskColumns): State => ({
      ...state,
      timesheetTaskColumns: Array.from(new Set(timesheetTaskColumns)),
    })
  )
  .case(actionTypes.fetchedWidgetMetadata, reducerFuncs.fetchedWidgetMetadata)
  .case(actionTypes.clearEntityFetchStatus, (state): State => {
    const entityKeys = Object.values(EntityDataKey);
    return {
      ...state,
      draft: {
        ...state.draft,
        hasLoadedInitialData: false,
      },
      xhrStatus: {
        ...state.xhrStatus,
        fetched: state.xhrStatus.fetched.filter(s => !entityKeys.includes(s.key as EntityDataKey)),
      },
    };
  })
  .case(
    actionTypes.setPublicReportTimestamp,
    (state, publicReportTimestamp): State => ({
      ...state,
      publicReportTimestamp,
    })
  )
  .case(actionTypes.synthesizeProjectsFromHours, reducerFuncs.synthesizeProjectsFromHours)
  .case(
    actionTypes.enforceMinimumWidgetWidth,
    (state, minimumWidgetWidth): State => ({
      ...state,
      minimumWidgetWidth,
    })
  )
  .case(
    actionTypes.fetchedReportExportSettings,
    (state, reportExportSettings): State => ({
      ...state,
      reportExportSettings,
    })
  )
  .case(
    actionTypes.fetchedNextExportDate,
    (state, { scheduled_at }): State => ({
      ...state,
      nextExportDate: scheduled_at,
    })
  )
  .case(actionTypes.receiveReportExportFile, (state, file) => {
    const downloadedReports = [file, ...(state.draft.downloaded_reports?.slice(0, 4) || [])];
    return { ...state, draft: { ...state.draft, downloaded_reports: downloadedReports } };
  });

export default actionTypes.withXhrStatusReducer(actionTypes.withBatchSelectionsReducer(reducer));
